import { GridFilterItem, GridFilterModel } from '@mui/x-data-grid-pro';
import { ReactNode, useReducer } from 'react';
import { useLocation, useSearchParams } from 'react-router';

import { GridStateOptions } from './DefaultPageStates';
import { AdministrationTableContext } from './TableProviderContext';
import { defaultGlobalTableStates } from './DefaultPageStates';

function parseRouteDefaults(
  params: URLSearchParams,
): GridStateOptions | undefined {
  const paramsSort = params.get('sort');
  const paramsFilter = params.get('filters');

  if (!paramsSort && !paramsFilter) {
    return undefined;
  }

  try {
    const defaults: GridStateOptions = {};

    if (paramsSort) {
      const sorting = JSON.parse(paramsSort);
      if (sorting.field && sorting.sort) {
        defaults.sort = [sorting];
      }
    }

    if (paramsFilter) {
      const filter: GridFilterItem[] = JSON.parse(paramsFilter);
      const valid = filter.every(
        ({ field, operator, value }) => !!field && !!operator && !!value,
      );
      if (valid) {
        defaults.filter = {
          items: filter.map((item, index) => ({
            ...item,
            id: item.id ?? index,
          })),
        } as GridFilterModel;
      }
    }

    return Object.keys(defaults).length > 0 ? defaults : undefined;
  } catch {
    return undefined;
  }
}

const defaultGlobalState = defaultGlobalTableStates();

const TableProvider = ({ children }: { children: ReactNode }) => {
  const { StateContext, DispatchContext } = AdministrationTableContext;

  const { pathname } = useLocation();
  const [params] = useSearchParams();

  const tabName = pathname
    .split('/')
    .filter(Boolean)
    .reverse()
    .find((segment) => segment in defaultGlobalState);

  const routeDefaults = parseRouteDefaults(params);

  const [state, dispatch] = useReducer(
    (state, newValue) => {
      return { ...state, ...newValue };
    },
    tabName && routeDefaults
      ? {
          ...defaultGlobalState,
          [tabName]: { ...defaultGlobalState[tabName], ...routeDefaults },
        }
      : defaultGlobalState,
  );

  return (
    <StateContext.Provider value={state}>
      <DispatchContext.Provider value={dispatch}>
        {children}
      </DispatchContext.Provider>
    </StateContext.Provider>
  );
};

export default TableProvider;
